import React from "react";
import { Link } from "react-router-dom";
import {
  PhoneCall,
  Mic,
  Gauge,
  QrCode,
  ShieldCheck,
  Users,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  GraduationCap,
  Building2,
  Mail,
  Keyboard,
  LayoutDashboard,
  MessageSquare,
  HelpCircle,
  MapPin,
  HeartPulse,
  Phone,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Section, SectionLabel } from "@/components/Section";

const IMG_VOICE = "/screenshots/voice-select.png";
const IMG_CALL = "/screenshots/call-active.png";
const IMG_SCORE = "/screenshots/score-report.png";
const IMG_DASHBOARD = "/screenshots/trainer-dashboard.png";
const IMG_COURSE = "/screenshots/create-course.png";

/* ---------------------------------- Hero --------------------------------- */
function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#FAFAF8]" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -right-24 h-[480px] w-[480px] rounded-full bg-[#A7B584]/15 blur-3xl" />
        <div className="absolute top-1/2 -left-20 h-[320px] w-[320px] rounded-full bg-[#FCEBD6]/40 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        <div className="lg:col-span-7">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#FCEBD6] text-[#B36B15] tracking-wide"
            data-testid="hero-alpha-badge"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Alpha-Stand · Jetzt als Early Tester starten
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#5A5B5C] tracking-tight leading-[1.05]">
            Trainieren Sie den Ernstfall,
            <span className="block text-[#A7B584]">bevor er eintritt.</span>
          </h1>

          <p className="mt-6 text-lg text-[#272525] max-w-2xl leading-relaxed">
            Help4Emergency ist die Web-App für Erste-Hilfe-Kurse: Teilnehmende üben den Notruf
            112 in einer <strong className="text-[#5A5B5C]">realistischen KI-Sprachsimulation</strong>.
            QR-Code scannen, ins Mikrofon sprechen, alle 5 W-Fragen beantworten — und sofort
            ehrliches Feedback mit Score 0–100 erhalten.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="hero-register-btn">
              <Button className="w-full sm:w-auto bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg px-6 py-6 text-base font-semibold shadow-sm">
                Early-Tester werden
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#loesung" data-testid="hero-demo-btn">
              <Button
                variant="outline"
                className="w-full sm:w-auto bg-transparent text-[#272525] border-[#EAEBE6] hover:border-[#5A5B5C] hover:bg-[#F4F5F2] rounded-lg px-6 py-6 text-base font-semibold"
              >
                So funktioniert’s
              </Button>
            </a>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-[#5A5B5C]">
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#A7B584]" /> Kein Download
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#A7B584]" /> DSGVO-konform (EU)
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#A7B584]" /> Auto-Löschung nach 90 Tagen
            </span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative rounded-2xl overflow-hidden border border-[#EAEBE6] bg-white shadow-xl shadow-[#5A5B5C]/5">
            <img
              src={IMG_VOICE}
              alt="Hilfe4Emergency — Stimme wählen (Bella oder Daniel) für die Notruf-Simulation"
              className="w-full h-[480px] object-cover object-top"
              loading="eager"
            />
            <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-xl p-4 border border-[#EAEBE6] shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
                  <Mic className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#A7B584]">
                    Ihre virtuelle Leitstelle
                  </div>
                  <div className="mt-0.5 text-sm font-semibold text-[#5A5B5C]">
                    Bella oder Daniel — Sie wählen die Stimme
                  </div>
                  <div className="mt-1 text-xs text-[#5A5B5C]">
                    Freundlich · beruhigend · professionell
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-white border border-[#EAEBE6] rounded-xl px-4 py-3 shadow-md">
            <div className="h-2 w-2 rounded-full bg-[#A7B584] animate-pulse" />
            <span className="text-xs font-medium text-[#5A5B5C]">KI-Leitstelle · Live-Simulation</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Trust Bar ------------------------------- */
function TrustBar() {
  const items = [
    "Fahrschulen",
    "Hilfsorganisationen",
    "Unternehmen",
    "Erste-Hilfe-Kursleitende",
    "Betriebs-Ersthelfer",
    "Feuerwehren",
    "Kinder",
    "Erwachsene",
  ];
  return (
    <div className="border-y border-[#EAEBE6] bg-[#F4F5F2]/40" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5A5B5C]">
          Entwickelt mit & für
        </span>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#5A5B5C]">
          {items.map((i) => (
            <span key={i} className="opacity-80">{i}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- Problem -------------------------------- */
function ProblemSection() {
  return (
    <Section id="problem" className="bg-[#FAFAF8]">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5 relative">
          <div className="rounded-2xl overflow-hidden border border-[#EAEBE6] bg-white shadow-sm">
            <img
              src={IMG_CALL}
              alt="Live-Notruf-Simulation im Browser — Gespräch mit der virtuellen Leitstelle"
              className="w-full h-[460px] object-contain bg-white"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-5 -right-5 bg-white border border-[#EAEBE6] rounded-xl px-5 py-4 shadow-md hidden md:block">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A7B584] font-semibold">Im Browser</div>
            <div className="mt-1 font-semibold text-[#5A5B5C]">Kein Download nötig</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <SectionLabel>Das Problem</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
            Notruf-Wissen ist Theorie. Unter Stress fehlt die Routine.
          </h2>
          <p className="mt-5 text-lg text-[#272525] leading-relaxed max-w-2xl">
            In Erste-Hilfe-Kursen wird die 112 nur besprochen — selten realistisch geübt. Im
            echten Notfall sind Menschen nervös, vergessen wichtige Angaben und wissen nicht,
            was die Leitstelle von ihnen braucht. Das kostet im Ernstfall wertvolle Zeit.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Clock, t: "Verlorene Sekunden", d: "Zögern, Rückfragen, Unsicherheit verzögern die Disposition und das Eintreffen der Rettung." },
              { icon: MapPin, t: "Fehlende 5 W-Infos", d: "Ort, Was, Anzahl, Verletzungen, Rückruf — unter Druck wird die Hälfte vergessen." },
              { icon: HeartPulse, t: "Angst vor Fehlern", d: "Die Sorge, etwas falsch zu machen, hemmt die Helferbereitschaft — und den Notruf selbst." },
              { icon: Users, t: "Keine Wiederholung", d: "Im Kurs bleibt keine Zeit, dass jede Person den Notruf einmal realistisch übt." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-white border border-[#EAEBE6] rounded-xl p-6" data-testid={`problem-card-${t}`}>
                <Icon className="h-5 w-5 text-[#A7B584]" />
                <div className="mt-3 font-semibold text-[#5A5B5C]">{t}</div>
                <p className="mt-1.5 text-sm text-[#272525] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------- Solution ------------------------------- */
function SolutionSection() {
  const steps = [
    {
      n: "01",
      t: "Kurs anlegen",
      d: "Kursleitung erstellt in unter 2 Minuten einen Kurs und erhält ein Handout mit QR-Code per E-Mail.",
      icon: GraduationCap,
    },
    {
      n: "02",
      t: "QR-Code scannen",
      d: "Teilnehmende scannen den QR-Code im Kursraum — ohne App-Installation, direkt im Browser.",
      icon: QrCode,
    },
    {
      n: "03",
      t: "Registrieren & Stimme wählen",
      d: "Name, Einwilligung, Stimme der Leitstelle (Bella oder Daniel) — fertig in 30 Sekunden.",
      icon: Mic,
    },
    {
      n: "04",
      t: "Notruf simulieren",
      d: "Die KI-Leitstelle fragt die 5 W-Fragen ab, nimmt Stress raus und beendet beruhigend.",
      icon: PhoneCall,
    },
    {
      n: "05",
      t: "Score & Feedback",
      d: "Direkt nach dem Gespräch: Score 0–100 und 3 konkrete, ehrliche Feedback-Sätze per E-Mail.",
      icon: Gauge,
    },
  ];

  return (
    <Section id="loesung" className="bg-[#F4F5F2]/40 border-y border-[#EAEBE6]">
      <div className="max-w-3xl">
        <SectionLabel>Die Lösung · KI-Leitstelle</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
          Vier Schritte vom QR-Code zum echten Aha-Moment.
        </h2>
        <p className="mt-5 text-lg text-[#272525] leading-relaxed">
          Kein Rollenspiel, kein Papier — eine echte Sprachsimulation im Browser. Planbar für
          Kursleitende, motivierend für Teilnehmende, DSGVO-konform.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {steps.map(({ n, t, d, icon: Icon }) => (
          <div
            key={n}
            data-testid={`solution-step-${n}`}
            className="relative bg-white border border-[#EAEBE6] rounded-xl p-7 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#A7B584]">Schritt {n}</span>
              <Icon className="h-5 w-5 text-[#5A5B5C]" />
            </div>
            <div className="mt-4 text-lg font-semibold text-[#5A5B5C]">{t}</div>
            <p className="mt-2 text-sm text-[#272525] leading-relaxed">{d}</p>
          </div>
        ))}
      </div>

      <div className="mt-16 rounded-2xl border border-[#EAEBE6] bg-white p-6 lg:p-8 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#A7B584]">
            Die 5 W-Fragen
          </div>
          <h3 className="mt-2 text-2xl font-semibold text-[#5A5B5C]">
            Genau das, was die Leitstelle braucht.
          </h3>
          <p className="mt-3 text-[#272525] leading-relaxed max-w-xl">
            Help4Emergency trainiert exakt die Struktur, die im Ernstfall zählt — in der
            richtigen Reihenfolge, ruhig und vollständig.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {[
              ["1", "Wo", "ist der Notfallort?"],
              ["2", "Was", "ist passiert?"],
              ["3", "Wie viele", "Personen sind betroffen?"],
              ["4", "Welche", "Verletzungen / Symptome?"],
              ["5", "Rückruf", "— Ihre Telefonnummer."],
            ].map(([n, w, d]) => (
              <div key={n} className="flex items-start gap-3 p-3 rounded-lg bg-[#F4F5F2]/50">
                <div className="h-8 w-8 shrink-0 rounded-full bg-[#A7B584] grid place-items-center text-white text-sm font-semibold">
                  {n}
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-[#5A5B5C]">{w}</div>
                  <div className="text-[#272525]">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-xl overflow-hidden border border-[#EAEBE6] bg-white">
            <img
              src={IMG_SCORE}
              alt="Ihre Auswertung mit Score 85/100 und drei konkreten Feedback-Sätzen"
              className="w-full h-[420px] object-contain bg-white"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------- Features ------------------------------- */
function FeaturesBento() {
  return (
    <Section id="features" className="bg-[#FAFAF8]">
      <div className="max-w-3xl">
        <SectionLabel>Features</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
          Alles, was Kursleitende und Teilnehmende brauchen.
        </h2>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-6">
        {/* Big card — Live Dashboard */}
        <div className="md:col-span-4 bg-white border border-[#EAEBE6] rounded-2xl p-8 relative overflow-hidden" data-testid="feature-dashboard">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
              <LayoutDashboard className="h-5 w-5" />
            </div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#A7B584]">
              Live-Dashboard für Kursleitende
            </div>
          </div>
          <h3 className="mt-5 text-2xl font-semibold text-[#5A5B5C]">
            In Echtzeit sehen, wie Ihr Kurs performt.
          </h3>
          <p className="mt-3 text-[#272525] leading-relaxed max-w-xl">
            Anrufe, Scores, Ø-Gesprächsdauer, perfekte Durchläufe, häufigste Fehler — und jede
            teilnehmende Person namentlich. Plus QR-Code zum Teilen und „Report senden" per Klick.
          </p>
          <div className="mt-7 rounded-xl overflow-hidden border border-[#EAEBE6] bg-white">
            <img src={IMG_DASHBOARD} alt="Kursleitenden-Dashboard mit Live-Statistiken" className="w-full h-64 object-cover object-top" loading="lazy" />
          </div>
        </div>

        <div className="md:col-span-2 bg-white border border-[#EAEBE6] rounded-2xl p-8" data-testid="feature-score">
          <Gauge className="h-5 w-5 text-[#A7B584]" />
          <h3 className="mt-4 text-xl font-semibold text-[#5A5B5C]">Ehrlicher Score 0–100</h3>
          <p className="mt-2 text-sm text-[#272525] leading-relaxed">
            Keine Halluzination, keine Schönfärberei. Die KI bewertet nur, was tatsächlich
            gesagt wurde — mit 3 konkreten Feedback-Sätzen.
          </p>
          <div className="mt-5 rounded-lg overflow-hidden border border-[#EAEBE6]">
            <img src={IMG_SCORE} alt="Score 85/100 mit Feedback" className="w-full h-40 object-contain bg-white" loading="lazy" />
          </div>
        </div>

        <div className="md:col-span-3 bg-white border border-[#EAEBE6] rounded-2xl p-8" data-testid="feature-voices">
          <Mic className="h-5 w-5 text-[#A7B584]" />
          <h3 className="mt-4 text-xl font-semibold text-[#5A5B5C]">Zwei Stimmen · Bella & Daniel</h3>
          <p className="mt-2 text-sm text-[#272525] leading-relaxed">
            Realistische Sprachsynthese (ElevenLabs). Teilnehmende wählen weiblich oder
            männlich — freundlich, ruhig, professionell wie eine echte Leitstelle.
          </p>
        </div>

        <div className="md:col-span-3 bg-[#1E1F1A] text-white border border-[#1E1F1A] rounded-2xl p-8 relative overflow-hidden" data-testid="feature-qr">
          <QrCode className="h-5 w-5 text-[#A7B584]" />
          <h3 className="mt-4 text-xl font-semibold">Kurs anlegen &amp; QR-Code erhalten</h3>
          <p className="mt-2 text-sm text-white/70 leading-relaxed">
            Kursleitung füllt ein kurzes Formular aus — Kurstitel, Datum, Gültigkeit — und
            bekommt automatisch ein Handout mit QR-Code per E-Mail. Im Kursraum zeigen,
            Teilnehmende scannen, starten.
          </p>
          <div className="mt-6 rounded-xl overflow-hidden border border-white/10 bg-white">
            <img
              src={IMG_COURSE}
              alt="Neuen Kurs erstellen — Formular mit Name der Kursleitung, Kurstitel, Datum und E-Mail"
              className="w-full h-52 object-contain bg-white"
              loading="lazy"
            />
          </div>
        </div>

        <div className="md:col-span-2 bg-white border border-[#EAEBE6] rounded-2xl p-8" data-testid="feature-fallback">
          <Keyboard className="h-5 w-5 text-[#A7B584]" />
          <h3 className="mt-4 text-xl font-semibold text-[#5A5B5C]">Text-Fallback</h3>
          <p className="mt-2 text-sm text-[#272525] leading-relaxed">
            Mikrofon blockiert oder iPad leise? Teilnehmende können jederzeit per Texteingabe
            antworten — niemand bleibt zurück.
          </p>
        </div>

        <div className="md:col-span-2 bg-white border border-[#EAEBE6] rounded-2xl p-8" data-testid="feature-mail">
          <Mail className="h-5 w-5 text-[#A7B584]" />
          <h3 className="mt-4 text-xl font-semibold text-[#5A5B5C]">E-Mail-Report</h3>
          <p className="mt-2 text-sm text-[#272525] leading-relaxed">
            Individuelles Ergebnis für Teilnehmende, Kurs-Zusammenfassung für die Leitung —
            automatisch per E-Mail.
          </p>
        </div>

        <div className="md:col-span-2 bg-white border border-[#EAEBE6] rounded-2xl p-8" data-testid="feature-gdpr">
          <ShieldCheck className="h-5 w-5 text-[#A7B584]" />
          <h3 className="mt-4 text-xl font-semibold text-[#5A5B5C]">DSGVO-konform</h3>
          <p className="mt-2 text-sm text-[#272525] leading-relaxed">
            Einwilligung, Datenauskunft, Löschrecht, EU-Hosting, automatische Löschung nach
            90 Tagen.
          </p>
        </div>
      </div>
    </Section>
  );
}

/* ------------------------------ Audience -------------------------------- */
function AudienceSection() {
  const audiences = [
    {
      icon: GraduationCap,
      label: "Primär",
      title: "Erste-Hilfe-Kursleitende",
      desc: "Fahrschulen, Unternehmen, Hilfsorganisationen: Kurse erstellen, QR-Codes verteilen, Live-Dashboard nutzen, Reports automatisch versenden.",
      points: ["Kurs anlegen in < 2 Minuten", "Live-Statistiken & Fehlerauswertung", "Report per E-Mail an Teilnehmende"],
    },
    {
      icon: Users,
      label: "Nutzen",
      title: "Kursteilnehmende",
      desc: "Scannen, sprechen, lernen. Ohne Download, ohne Profil-Aufwand — mit sofortigem, ehrlichem Feedback.",
      points: ["QR-Code scannen", "Realistische Sprachsimulation", "Score + E-Mail-Report"],
    },
    {
      icon: Building2,
      label: "Skalieren",
      title: "Software-Provider",
      desc: "White-Label & Provider-Dashboard: alle Kurse, Statistiken und Nutzungsdaten an einem Ort — für Verbände und Plattformen.",
      points: ["Provider-Dashboard", "Alle Kurse im Überblick", "Zeitreihen & Nutzungsdaten"],
    },
  ];

  return (
    <Section id="zielgruppen" className="bg-[#F4F5F2]/40 border-y border-[#EAEBE6]">
      <div className="max-w-3xl">
        <SectionLabel>Zielgruppen</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
          Für alle, die Notrufe realistisch üben lassen wollen.
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {audiences.map(({ icon: Icon, label, title, desc, points }) => (
          <div key={title} data-testid={`audience-${title}`} className="bg-white border border-[#EAEBE6] rounded-2xl p-8 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
                <Icon className="h-5 w-5" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#A7B584] bg-[#A7B584]/10 px-2 py-1 rounded-full">
                {label}
              </span>
            </div>
            <h3 className="mt-5 text-xl font-semibold text-[#5A5B5C]">{title}</h3>
            <p className="mt-2 text-sm text-[#272525] leading-relaxed">{desc}</p>
            <ul className="mt-5 space-y-2.5">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-[#272525]">
                  <CheckCircle2 className="h-4 w-4 text-[#A7B584] shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* --------------------------- Trust / Evidence --------------------------- */
function TrustSection() {
  const pillars = [
    { t: "DSGVO & EU-Hosting", d: "Server in Europa, Auftragsverarbeitung, klare Einwilligung, Löschrecht." },
    { t: "Auto-Cleanup nach 90 Tagen", d: "Trainingsdaten werden automatisch gelöscht — kein manueller Aufwand nötig." },
    { t: "Keine Drittanbieter-Erkennung", d: "Spracherkennung läuft lokal im Browser (Web Speech API)." },
  ];
  return (
    <Section id="evidenz" className="bg-[#FAFAF8]">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <SectionLabel>Vertrauen & Datenschutz</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
            Mehr Praxis pro Person. Weniger Bürokratie.
          </h2>
          <p className="mt-5 text-lg text-[#272525] leading-relaxed max-w-xl">
            Jede Kursteilnehmerin übt den Notruf mindestens einmal realistisch — statt nur
            darüber zu reden. Ohne technische Hürde, ohne Datenschutz-Kopfzerbrechen.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-5">
            {[
              ["0–100", "Score pro Anruf"],
              ["5", "W-Fragen trainiert"],
              ["90 T.", "Auto-Löschung"],
            ].map(([v, l]) => (
              <div key={l} className="bg-white border border-[#EAEBE6] rounded-xl p-5">
                <div className="text-2xl font-semibold text-[#A7B584]">{v}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.14em] text-[#5A5B5C]">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 grid gap-4">
          {pillars.map((p) => (
            <div key={p.t} className="bg-white border border-[#EAEBE6] rounded-xl p-6 flex gap-4" data-testid={`evidence-${p.t}`}>
              <div className="h-10 w-10 shrink-0 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-[#5A5B5C]">{p.t}</div>
                <p className="mt-1 text-sm text-[#272525] leading-relaxed">{p.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------- Pricing ------------------------------- */
function PricingSection() {
  const tiers = [
    {
      name: "Kurs-Paket",
      desc: "Pro Kurs abrechenbar. Ideal für einzelne Erste-Hilfe-Kurse, BG-Termine oder Pilotgruppen.",
      perfect: "Einzelne Kurse & Erst-Tests",
      featured: false,
    },
    {
      name: "Unternehmens- & Standort-Lizenz",
      desc: "Unbegrenzt Kurse pro Jahr für einen Standort oder ein Unternehmen.",
      perfect: "Fahrschulen · Unternehmen · Ausbildungsstätten",
      featured: true,
    },
    {
      name: "Provider-Lizenz",
      desc: "White-Label & Provider-Dashboard über alle Kurse — für Verbände und Plattformanbieter.",
      perfect: "Hilfsorganisationen · Software-Provider",
      featured: false,
    },
  ];
  return (
    <Section id="preise" className="bg-[#F4F5F2]/40 border-y border-[#EAEBE6]">
      <div className="max-w-3xl">
        <SectionLabel>Preise</SectionLabel>
        <h2 className="text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
          Planbar — vom Einzelkurs bis zum Provider-Rollout.
        </h2>
        <p className="mt-5 text-lg text-[#272525] leading-relaxed">
          In der Alpha stellen wir den Zugang ausgewählten Early-Testern kostenfrei bereit —
          gegen ehrliches Feedback. Für die Preise nach Launch sprechen wir gerne individuell.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {tiers.map((t) => (
          <div
            key={t.name}
            data-testid={`pricing-${t.name}`}
            className={`rounded-2xl p-8 border transition-all ${
              t.featured
                ? "bg-[#1E1F1A] text-white border-[#1E1F1A] shadow-xl shadow-black/10 -translate-y-1"
                : "bg-white border-[#EAEBE6] hover:shadow-md"
            }`}
          >
            {t.featured && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-[#A7B584] text-[#1E1F1A] tracking-wide mb-4">
                Am beliebtesten
              </div>
            )}
            <div className={`text-lg font-semibold ${t.featured ? "text-white" : "text-[#5A5B5C]"}`}>
              {t.name}
            </div>
            <p className={`mt-2 text-sm leading-relaxed ${t.featured ? "text-white/70" : "text-[#272525]"}`}>
              {t.desc}
            </p>
            <div className={`mt-6 text-xs uppercase tracking-[0.14em] ${t.featured ? "text-[#A7B584]" : "text-[#A7B584]"}`}>
              Passt zu
            </div>
            <div className={`mt-1 text-sm ${t.featured ? "text-white" : "text-[#5A5B5C]"}`}>
              {t.perfect}
            </div>

            <Link to="/register" data-testid={`pricing-cta-${t.name}`}>
              <Button
                className={`mt-7 w-full rounded-lg ${
                  t.featured
                    ? "bg-[#A7B584] hover:bg-[#8C9A69] text-[#1E1F1A]"
                    : "bg-white border border-[#EAEBE6] text-[#272525] hover:bg-[#F4F5F2]"
                }`}
              >
                Als Early-Tester starten
              </Button>
            </Link>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------------------------- FAQ --------------------------------- */
function FaqSection() {
  const qas = [
    {
      q: 'Was bedeutet „Alpha-Stand" konkret für mich als Tester:in?',
      a: "Die Kernfunktionen laufen, neue Szenarien und Feedback-Signale kommen laufend dazu. Als Early Tester:in erhalten Sie kostenfrei Zugang zur App, können Bugs über die Support-Seite melden und die Roadmap direkt mitgestalten.",
    },
    {
      q: "Brauchen Teilnehmende einen Download oder Login?",
      a: "Nein. Teilnehmende scannen den QR-Code des Kurses, geben Ihren Namen und ihre Einwilligung ein und starten sofort — die App läuft im Browser (iOS Safari & Chrome getestet).",
    },
    {
      q: "Was ist, wenn das Mikrofon nicht funktioniert?",
      a: "Kein Problem — Help4Emergency bietet jederzeit den Text-Fallback: Teilnehmende können ihre Antworten tippen. Die KI-Leitstelle bewertet Inhalt und Vollständigkeit genauso fair.",
    },
    {
      q: "Welche KI steckt dahinter? Werden meine Daten zum Training verwendet?",
      a: "Die Gesprächslogik läuft auf Claude Sonnet 4.5, die Stimmen stammen von ElevenLabs. Daten werden nicht zum Modelltraining verwendet. Gespräche werden nach 90 Tagen automatisch gelöscht.",
    },
    {
      q: "Ist die Plattform DSGVO-konform?",
      a: "Ja. Hosting in der EU, Auftragsverarbeitung, Einwilligung, Datenauskunft, Löschrecht, Auto-Cleanup nach 90 Tagen.",
    },
    {
      q: "Ersetzt Help4Emergency den Erste-Hilfe-Kurs?",
      a: "Nein. Help4Emergency ergänzt bestehende Kurse durch echte Notruf-Telefonroutine — die praktische Lücke zwischen Theorie und Ernstfall.",
    },
  ];
  return (
    <Section id="faq" className="bg-[#FAFAF8]">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
            Häufige Fragen.
          </h2>
          <p className="mt-5 text-[#272525] leading-relaxed">
            Noch etwas offen? Unser Support-Team antwortet in der Regel innerhalb eines Werktags.
          </p>
          <Link to="/support" className="mt-6 inline-flex items-center gap-2 text-[#A7B584] hover:text-[#8C9A69] font-semibold">
            Support kontaktieren <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {qas.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#EAEBE6]">
                <AccordionTrigger
                  className="text-left text-[#5A5B5C] font-semibold hover:no-underline py-5"
                  data-testid={`faq-trigger-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#272525] leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </Section>
  );
}

/* ----------------------------- Final CTA -------------------------------- */
function FinalCta() {
  return (
    <section className="bg-[#1E1F1A] text-white" data-testid="final-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#A7B584]/20 text-[#A7B584] tracking-wide">
            <Sparkles className="h-3.5 w-3.5" />
            Alpha · Early-Tester-Programm
          </div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Machen Sie Ihren nächsten Kurs zum <span className="text-[#A7B584]">echten Training</span>.
          </h2>
          <p className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">
            Kostenfreier Alpha-Zugang für Kursleitende, Organisationen und Provider. Sie legen
            den ersten Kurs in unter 2 Minuten an.
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-3">
          <Link to="/register" data-testid="final-cta-register">
            <Button className="w-full bg-[#A7B584] hover:bg-[#8C9A69] text-[#1E1F1A] rounded-lg px-6 py-6 text-base font-semibold">
              Jetzt Early-Tester werden <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/support" data-testid="final-cta-support">
            <Button
              variant="outline"
              className="w-full bg-transparent text-white border-white/20 hover:bg-white/5 hover:text-white rounded-lg px-6 py-6 text-base font-semibold"
            >
              Frage stellen
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- Page -------------------------------- */
export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero />
      <TrustBar />
      <ProblemSection />
      <SolutionSection />
      <FeaturesBento />
      <AudienceSection />
      <TrustSection />
      <PricingSection />
      <FaqSection />
      <FinalCta />
    </div>
  );
}
